/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./Profile.css";

// Our profile · the account's own answers about what it works on and what it
// is looking for.
//
// Laid out as the v2 desktop-1440 artboard has it: a main column of cards
// beside a 380px rail carrying the things that comment on them — why the
// questions are asked, how far through setup the account is, and the
// notification and privacy switches.
//
// The section is a shell over vm.profile.parts. Parts still to come are an
// entry in PROFILE_PARTS and a case in renderPart, with nothing about this
// component's structure or the view model's shape to change. The rail is part
// of the shell rather than a part of its own, because it is furniture around
// the column rather than a card in it.
class Profile extends Component {
  constructor(props) {
    super(props);
    this.missionCard = React.createRef();
  }

  /**
   * Bring the questions card into view when something sent the visitor here
   * to answer them — the profile-completeness banner is the one that does.
   *
   * "Our mission profile" is the last of four cards, so arriving at the top
   * of the page reads as the button having done nothing. Same reasoning as
   * NonprofitProof's composer scroll, and the same deferral: the card is
   * mounting in this commit, so its position is not settled until the browser
   * has laid it out.
   *
   * The request is cleared once honoured, so coming back to the profile later
   * leaves the page where the visitor last put it rather than snapping down.
   */
  componentDidMount() {
    this.scrollToRequested();
  }

  componentDidUpdate(prev) {
    if (prev.vm.profile.scrollTo !== this.props.vm.profile.scrollTo) {
      this.scrollToRequested();
    }
  }

  scrollToRequested() {
    const p = this.props.vm.profile;
    if (p.scrollTo !== "questions") {
      return;
    }
    const node = this.missionCard.current;
    if (node && typeof node.scrollIntoView === "function") {
      window.requestAnimationFrame(() => {
        const current = this.missionCard.current;
        if (current) {
          current.scrollIntoView({
            // Tour placement is measured while the scroll happens. Use an
            // immediate move for its explicit alignment so a smooth scroll
            // cannot carry the anchor past the measured panel; ordinary
            // profile CTAs retain the softer transition.
            behavior: p.scrollBlock ? "auto" : "smooth",
            block: p.scrollBlock || "start",
          });
        }
      });
    }
    // Cleared whether or not the node was there to scroll to: a request that
    // cannot be honoured must not sit in state waiting to fire on a later
    // render the visitor did not ask for.
    if (typeof p.onScrolled === "function") {
      p.onScrolled();
    }
  }

  // Every card opens the same way: a title, optionally something beside it,
  // and optionally a note pushed to the right.
  renderCardHead(title, chip, aside) {
    return (
      <div className="Marketplace-Profile-cardHead">
        <h2 className="Marketplace-Profile-cardTitle">{title}</h2>
        {chip}
        {aside && <span className="Marketplace-Profile-cardAside">{aside}</span>}
      </div>
    );
  }

  /**
   * Account details. The fields are the organisation record, which is set up
   * and changed in CheckPlus — so they are laid out as the design has them but
   * not editable here, and the footer says where they change rather than
   * offering a Save with nothing to write to.
   */
  renderAccount(vm) {
    const a = vm.profile.account;
    if (!a.rows.length) {
      return null;
    }
    return (
      <div className="Marketplace-Profile-card" key="account">
        {this.renderCardHead(a.title)}
        <div className="Marketplace-Profile-cardBlurb">{a.blurb}</div>
        <div className="Marketplace-Profile-fields">
          {a.rows.map((row) => (
            <div className="Marketplace-Profile-field" key={row.id}>
              <div className="Marketplace-Profile-fieldLabel">{row.label}</div>
              <input
                className="Marketplace-Profile-input"
                type="text"
                value={row.missing ? "" : row.value}
                placeholder={row.placeholder}
                readOnly
              />
              {row.note && (
                <div className="Marketplace-Profile-fieldNote">{row.note}</div>
              )}
            </div>
          ))}
        </div>
        <div className="Marketplace-Profile-cardFoot">
          {/* A donor has no account page on either platform to be sent to, so
              the footer carries the note alone rather than a pill pointing at
              an organisation sign-in they cannot use. */}
          {!!a.cta && !!a.href && (
            <a
              className="Marketplace-Profile-pill"
              href={a.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {a.cta}
            </a>
          )}
          <span className="Marketplace-Profile-footNote">{a.note}</span>
        </div>
      </div>
    );
  }

  /**
   * What the introduction's status line says, which is the one thing on the
   * card that reports the write rather than inviting it.
   *
   * A failed save outranks everything: it is the only state the account has
   * to do something about. "Unsaved changes" is next, because it is the one
   * that says the Save beside it is waiting. Nothing is said while the save
   * is in flight — the button is already saying it.
   */
  introState(i) {
    if (i.error) {
      return i.errorNote;
    }
    if (i.busy) {
      return "";
    }
    if (i.dirty) {
      return i.dirtyNote;
    }
    return i.saved ? i.savedNote : "";
  }

  /**
   * Introduction, with the logo and cover controls as its footer — the design
   * keeps the images beside the words about the organisation rather than in a
   * card of their own.
   *
   * The field commits on an explicit Save rather than on blur. It used to
   * write itself the moment focus left, which is the one behaviour a Discard
   * cannot coexist with — pressing it would have blurred the field and saved
   * the very draft it was there to throw away. It also matches the same
   * paragraph's editor on Impact Overview, which has always saved this way.
   */
  renderIntro(vm) {
    const i = vm.profile.intro;
    const b = vm.profile.brand;
    const canSave = i.dirty && !i.busy;
    const state = this.introState(i);
    return (
      <div className="Marketplace-Profile-card" key="intro">
        {this.renderCardHead(vm.m.profileIntroTitle)}
        <div className="Marketplace-Profile-cardBlurb">{i.label}</div>
        <textarea
          className={
            "Marketplace-Profile-textarea" +
            (i.busy ? " Marketplace-Profile-textarea--busy" : "")
          }
          maxLength={280}
          value={i.value}
          placeholder={i.placeholder}
          onChange={i.onText}
        />
        <div className="Marketplace-Profile-hint">{i.hint}</div>
        {/* Nonprofit only: this paragraph is also the introduction on Impact
            Overview, and a nonprofit editing it here should not have to find
            that out by noticing the other page changed. */}
        {!!i.linkedNote && (
          <div className="Marketplace-Profile-introLinked">
            {i.linkedNote}{" "}
            <span
              className="Marketplace-Profile-introLink"
              onClick={i.openLinked}
              role="button"
            >
              {i.linkedCta}
            </span>
          </div>
        )}
        <div className="Marketplace-Profile-introActions">
          <span
            className={
              "Marketplace-Profile-introSave" +
              (canSave ? "" : " Marketplace-Profile-introSave--off")
            }
            onClick={canSave ? i.onSave : undefined}
            role="button"
            aria-disabled={!canSave}
          >
            {i.busy ? i.savingBtn : i.saveBtn}
          </span>
          {/* Offered only while there is a draft to throw away. A Discard
              beside a saved field would either do nothing or read as a way
              to delete the introduction, and it is neither. */}
          {canSave && (
            <span
              className="Marketplace-Profile-introDiscard"
              onClick={i.onDiscard}
              role="button"
            >
              {i.discardBtn}
            </span>
          )}
          {!!state && (
            <span
              className={
                "Marketplace-Profile-introState" +
                (i.error ? " Marketplace-Profile-introState--bad" : "")
              }
            >
              {state}
            </span>
          )}
        </div>
        <div className="Marketplace-Profile-cardFoot">
          <span className="Marketplace-Profile-footBlurb">{b.blurb}</span>
          {/* The logo is changed here now. The cover still is not: it used to
              be uploaded from this pill and that is what came out — it picked
              a file and sent it on the spot, with no preview and no
              confirmation. The cover modal on Impact Overview previews the
              image, saves on an explicit button and shows what went wrong,
              which is the one place a cover should be changed from.

              A logo needs none of that: it is a small square shown at one
              size, and the pair of notes under this row carries both the
              warning and whatever went wrong.

              A donor has neither an organisation record for a logo nor a
              public page for a cover to sit behind, so both come from the
              view model rather than being drawn unconditionally. */}
          {b.canChangeLogo && (
            <label
              className={
                "Marketplace-Profile-pill" +
                (b.logoUploading ? " Marketplace-Profile-pill--busy" : "")
              }
            >
              {b.logoUploading ? b.logoState : b.logoBtn}
              <input
                type="file"
                className="Marketplace-Profile-fileInput"
                accept="image/jpeg,image/png"
                onChange={b.onPickLogo}
                disabled={b.logoUploading}
              />
            </label>
          )}
          {b.showCover && (
            <span className="Marketplace-Profile-pill" onClick={b.openCover}>
              {b.coverBtn}
            </span>
          )}
        </div>
        {/* Said before the picker is opened, not after the fact: for a
            nonprofit this picture is the organisation record's, so it changes
            on CheckPlus too. Empty for a business, which has no CheckPlus
            presence to affect. */}
        {b.canChangeLogo && !!b.logoShared && (
          <div className="Marketplace-Profile-logoShared">{b.logoShared}</div>
        )}
        {b.canChangeLogo && !b.logoUploading && !!b.logoState && (
          <div
            className={
              "Marketplace-Profile-logoState" +
              (b.logoBad ? " Marketplace-Profile-logoState--bad" : "")
            }
          >
            {b.logoState}
          </div>
        )}
      </div>
    );
  }

  // Charity status and payouts — shown, never edited here.
  renderCheckplus(vm) {
    const c = vm.profile.checkplus;
    if (!c.rows.length) {
      return null;
    }
    return (
      <div
        className="Marketplace-Profile-card"
        key="checkplus"
        data-tour="profile-checkplus"
      >
        {this.renderCardHead(
          c.title,
          <span className="Marketplace-Profile-chip Marketplace-Profile-chip--cp">
            {vm.m.profileCheckplusChip}
          </span>
        )}
        <div className="Marketplace-Profile-cardBlurb">
          {vm.m.profileCheckplusBlurb}
        </div>
        <div className="Marketplace-Profile-cpRows">
          {c.rows.map((row) => (
            <div className="Marketplace-Profile-cpRow" key={row.id}>
              <div className="Marketplace-Profile-cpMain">
                <div className="Marketplace-Profile-cpTitle">{row.title}</div>
                <div className="Marketplace-Profile-cpBody">{row.body}</div>
              </div>
              <span
                className="Marketplace-Profile-cpStatus"
                style={{ background: row.tint, color: row.color }}
              >
                {row.status}
              </span>
              <a
                className="Marketplace-Profile-cpCta"
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {row.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * One question: a field carrying its own answer, which opens a panel of
   * options below it. The whole set stays readable at a glance and only the
   * one being answered takes up room.
   */
  renderQuestion(vm, q) {
    return (
      <div className="Marketplace-Profile-q" key={q.id}>
        <div className="Marketplace-Profile-qHead">
          <span
            className="Marketplace-Profile-qNum"
            style={
              q.answered
                ? { background: "#e6f5ff", color: "#175b89" }
                : { background: "#f0f2f4", color: "#9aa0a6" }
            }
          >
            {q.num}
          </span>
          <span className="Marketplace-Profile-qTitle">{q.title}</span>
          <span className="Marketplace-Profile-qHint">{q.hint}</span>
        </div>

        {q.isText && (
          <input
            className="Marketplace-Profile-input Marketplace-Profile-input--q"
            type="text"
            maxLength={140}
            value={q.value}
            placeholder={q.placeholder}
            onChange={q.onText}
            onBlur={q.onTextBlur}
          />
        )}

        {q.isChips && (
          <div className="Marketplace-Profile-select">
            <div
              className="Marketplace-Profile-selectBox"
              style={{ borderColor: q.selBd }}
              onClick={q.toggle}
              role="button"
              aria-expanded={q.open}
            >
              <div className="Marketplace-Profile-picks">
                {q.isEmpty ? (
                  <span className="Marketplace-Profile-placeholder">
                    {q.emptyLabel}
                  </span>
                ) : (
                  q.picks.map((pick) => (
                    <span className="Marketplace-Profile-pick" key={pick.id}>
                      {pick.label}
                      {/* Removes without opening the panel — see the
                          stopPropagation in the view model. */}
                      <span
                        className="Marketplace-Profile-pickX"
                        onClick={pick.remove}
                        role="button"
                        aria-label={pick.label}
                      >
                        ×
                      </span>
                    </span>
                  ))
                )}
              </div>
              {q.countLabel && (
                <span className="Marketplace-Profile-count">{q.countLabel}</span>
              )}
              <span className="Marketplace-Profile-chev">{q.chev}</span>
            </div>

            {q.open && (
              <div className="Marketplace-Profile-panel">
                {/* Present for the whole life of a capped question, and
                    worded differently once the cap is reached. It used to
                    appear only at the cap, so the limit was something a
                    nonprofit found out by being stopped by it -- a tap on an
                    unpicked option simply did nothing, which reads as a tap
                    that missed rather than as a limit. */}
                {q.capNote && (
                  <div className="Marketplace-Profile-capNote">{q.capNote}</div>
                )}
                <div className="Marketplace-Profile-opts">
                  {q.options.map((o) => (
                    <span
                      className={
                        "Marketplace-Profile-opt" +
                        (o.on ? " Marketplace-Profile-opt--on" : "")
                      }
                      onClick={o.pick}
                      role="checkbox"
                      aria-checked={o.on}
                      key={o.id}
                    >
                      {o.on ? "✓ " : ""}
                      {o.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderQuestions(vm) {
    const p = vm.profile;
    if (p.state.loading) {
      return <div className="Marketplace-Profile-state">{vm.m.proofLoading}</div>;
    }
    if (p.state.error) {
      return (
        <div className="Marketplace-Profile-state">
          {vm.m.proofSectionError}
          <span className="Marketplace-Profile-retry" onClick={p.state.retry}>
            {vm.m.proofRetry}
          </span>
        </div>
      );
    }
    return (
      <div className="Marketplace-Profile-qs">
        {p.questions.map((q) => this.renderQuestion(vm, q))}
        {/* The open field sits inside the set but outside the count — see
            PROFILE_FREEFORM_ID. */}
        <div className="Marketplace-Profile-freeform">
          <div className="Marketplace-Profile-qTitle">{p.freeform.label}</div>
          <textarea
            className={
              "Marketplace-Profile-textarea Marketplace-Profile-textarea--tall" +
              (p.freeform.busy ? " Marketplace-Profile-textarea--busy" : "")
            }
            maxLength={280}
            value={p.freeform.value}
            placeholder={p.freeform.placeholder}
            onChange={p.freeform.onText}
            onBlur={p.freeform.onBlur}
          />
        </div>
      </div>
    );
  }

  renderMission(vm) {
    const p = vm.profile;
    // Two things want this element: the guided tour spotlights it (CP-308),
    // and "Complete my profile" scrolls to it (CP-319).
    return (
      <div
        className="Marketplace-Profile-card"
        key="questions"
        data-tour="profile-mission"
      >
        {/* Scroll by the compact introduction while spotlighting the complete
            card. Aligning the six-question card itself is unreliable because
            it is taller than the viewport; using this short anchor keeps the
            heading and the first questions in view together. */}
        <div
          className="Marketplace-Profile-tourAnchor"
          data-tour-scroll="profile-mission"
          ref={this.missionCard}
        >
          {this.renderCardHead(
            p.questionsPartLabel,
            <span
              className="Marketplace-Profile-chip"
              style={{ background: p.badgeBg, color: p.badgeFg }}
            >
              {p.doneLabel}
            </span>,
            p.skipNote
          )}
          <div className="Marketplace-Profile-cardBlurb">{p.partBlurb}</div>
          <div className="Marketplace-Profile-meter">
            <div
              className="Marketplace-Profile-meterFill"
              style={{ width: p.pctW }}
            />
          </div>
          {p.saveError && (
            <div className="Marketplace-Profile-error">{p.saveError}</div>
          )}
        </div>
        {this.renderQuestions(vm)}
        {/* Every answer saves as it is given, so there is no Save to press —
            what the design puts beside one is the note saying where the
            answers go, and that is what earns the space. */}
        <div className="Marketplace-Profile-saveNote">{p.saveNote}</div>
      </div>
    );
  }

  renderPart(vm, id) {
    switch (id) {
      case "account":
        return this.renderAccount(vm);
      case "intro":
        return this.renderIntro(vm);
      case "questions":
        return this.renderMission(vm);
      case "checkplus":
        return this.renderCheckplus(vm);
      default:
        // A part named in PROFILE_PARTS with no renderer yet. Nothing is a
        // better answer here than a placeholder that ships to production.
        return null;
    }
  }

  // The rail: what comments on the column rather than sitting in it.
  renderRail(vm) {
    const p = vm.profile;
    return (
      <div className="Marketplace-Profile-rail">
        <div className="Marketplace-Profile-why">
          <div className="Marketplace-Profile-railTitle Marketplace-Profile-railTitle--why">
            {p.whyTitle}
          </div>
          <div className="Marketplace-Profile-whyBody">{p.whyBody}</div>
        </div>

        {!!p.checklist.length && (
          <div className="Marketplace-Profile-railCard">
            <div className="Marketplace-Profile-railTitle">
              {vm.m.profileChecklistTitle}
            </div>
            <div className="Marketplace-Profile-checks">
              {p.checklist.map((row) => (
                <div className="Marketplace-Profile-check" key={row.id}>
                  <span
                    className="Marketplace-Profile-checkMark"
                    style={{
                      background: row.markBg,
                      borderColor: row.markBd,
                      color: row.markFg,
                    }}
                  >
                    {row.mark}
                  </span>
                  <span
                    className="Marketplace-Profile-checkLabel"
                    style={{ fontWeight: row.weight }}
                  >
                    {row.label}
                  </span>
                  <span className="Marketplace-Profile-checkState">
                    {row.state}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!!p.showPrefs && !!p.prefs.length && (
          <div className="Marketplace-Profile-railCard">
            <div className="Marketplace-Profile-railTitle">
              {vm.m.profilePrefsTitle}
            </div>
            <div className="Marketplace-Profile-prefs">
              {p.prefs.map((pref) => (
                <div
                  className={
                    "Marketplace-Profile-pref" +
                    (pref.busy ? " Marketplace-Profile-pref--busy" : "")
                  }
                  onClick={pref.busy ? undefined : pref.toggle}
                  role="switch"
                  aria-checked={pref.on}
                  aria-label={pref.label}
                  key={pref.id}
                >
                  <span
                    className="Marketplace-Profile-switch"
                    style={{ background: pref.trackBg }}
                  >
                    <span
                      className="Marketplace-Profile-switchKnob"
                      style={{ left: pref.knobX }}
                    />
                  </span>
                  <span className="Marketplace-Profile-prefLabel">
                    {pref.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const vm = this.props.vm;
    return (
      <div className="Marketplace-Profile">
        <div className="Marketplace-Profile-main">
          {vm.profile.parts.map((id) => this.renderPart(vm, id))}
        </div>
        {this.renderRail(vm)}
      </div>
    );
  }
}

Profile.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default Profile;
